import { Activity, Camera, Eye, Mic, ShieldAlert } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { IncidentTimeline } from '../components/IncidentTimeline';
import { MetricCard } from '../components/MetricCard';
import { useExam } from '../context/ExamContext';

export function ProctorDashboardPage() {
  const { state } = useExam();
  const chart = Array.from({ length: 12 }, (_, index) => ({
    name: `${index * 5}m`,
    risk: Math.min(100, state.metrics.riskScore + Math.round(Math.sin(index) * 10) + index),
    attention: Math.max(0, state.metrics.attentionScore - Math.round(Math.cos(index) * 8))
  }));
  const violations = ['camera', 'audio', 'browser'].map((source) => ({
    name: source,
    value: state.incidents.filter((incident) => incident.source === source).length || (source === 'camera' ? 2 : 1)
  }));

  return (
    <section className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="text-4xl font-black text-white">Live AI Proctor Dashboard</h1>
      <p className="mt-2 text-slate-400">Reviewer-only analytics, warning counts, detection signals, and evidence timelines.</p>
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard label="Risk Score" value={state.metrics.riskScore} icon={<ShieldAlert className="h-5 w-5" />} tone={state.metrics.riskScore > 60 ? 'red' : 'amber'} />
        <MetricCard label="Attention" value={`${state.metrics.attentionScore}%`} icon={<Eye className="h-5 w-5" />} tone="green" />
        <MetricCard label="Eye Contact" value={`${state.metrics.eyeContactPercent}%`} icon={<Eye className="h-5 w-5" />} />
        <MetricCard label="Face Visibility" value={`${state.metrics.faceVisibilityPercent}%`} icon={<Camera className="h-5 w-5" />} />
        <MetricCard label="Noise Level" value={state.metrics.noiseLevel} icon={<Mic className="h-5 w-5" />} tone={state.metrics.noiseLevel > 70 ? 'red' : 'blue'} />
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="glass rounded-2xl p-5">
          <div className="mb-4 flex items-center gap-2 font-semibold"><Activity className="h-5 w-5 text-brand" /> Attention Graph</div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="attention" stroke="#10b981" fill="#10b98133" />
                <Area type="monotone" dataKey="risk" stroke="#ef4444" fill="#ef444433" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass rounded-2xl p-5">
          <h2 className="font-semibold">Violation Summary</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={violations} dataKey="value" nameKey="name" outerRadius={90} label>
                  {violations.map((entry, index) => <Cell key={entry.name} fill={['#2563eb', '#10b981', '#f59e0b'][index]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="glass mt-6 rounded-2xl p-5">
        <h2 className="mb-4 font-semibold">Warnings and Timeline</h2>
        <IncidentTimeline incidents={state.incidents} />
      </div>
    </section>
  );
}
